/**
 * Projectile bullets — flight, wall collision, safety age-out. Headless
 * (no PixiJS). See CLAUDE.md architecture rule 4.
 *
 * A bullet carries an optional pre-resolved BulletHitPlan (limb + damage
 * baked in at fire time by the reticule). When the bullet reaches the
 * planned target tile without being stopped by a wall, the caller's
 * onImpact hook applies the plan. Everything upstream of onImpact is
 * pure geometry — no combat rules leak into this module.
 */

import { BULLET_MAX_AGE_SEC, BULLET_SPEED_TILES_PER_SEC } from '../render/style';
import type { BodyPart } from './body';
import { damageTile, inBounds, tileAt, type MapData } from './map';
import { tileStopsBullet } from './tiles';

export interface BulletHitPlan {
  /** Entity id the shot was aimed at. */
  targetId: number;
  /** Which limb the reticule picked. null = reticule was off the silhouette
   *  (pure miss — bullet still flies but does no damage on arrival). */
  limb: BodyPart | null;
  damage: number;
}

export interface Bullet {
  id: number;
  /** Position in tile units (float). */
  x: number;
  y: number;
  /** Velocity in tiles/sec. */
  vx: number;
  vy: number;
  /** Elapsed seconds since spawn. */
  age: number;
  /** Attacker entity id — never resolves against the source. */
  sourceId: number;
  /** Tile the bullet is flying toward (reticule pick / target center). */
  targetX: number;
  targetY: number;
  /** Damage plan baked in at fire time. */
  plan: BulletHitPlan | null;
  alive: boolean;
}

let nextBulletId = 1;

/** Reset the internal bullet-id counter (test hook). */
export function resetBulletIds(): void {
  nextBulletId = 1;
}

export interface SpawnBulletArgs {
  fromX: number;
  fromY: number;
  targetX: number;
  targetY: number;
  sourceId: number;
  plan?: BulletHitPlan;
  /** Override travel speed (tiles/sec). Defaults to style.ts constant. */
  speed?: number;
}

/** Build a Bullet flying from (fromX, fromY) toward (targetX, targetY). */
export function spawnBullet(args: SpawnBulletArgs): Bullet {
  const dx = args.targetX - args.fromX;
  const dy = args.targetY - args.fromY;
  const len = Math.hypot(dx, dy) || 1;
  const speed = args.speed ?? BULLET_SPEED_TILES_PER_SEC;
  return {
    id: nextBulletId++,
    x: args.fromX,
    y: args.fromY,
    vx: (dx / len) * speed,
    vy: (dy / len) * speed,
    age: 0,
    sourceId: args.sourceId,
    targetX: args.targetX,
    targetY: args.targetY,
    plan: args.plan ?? null,
    alive: true,
  };
}

// `tileStopsBullet` lives in the tile trait table; re-exported here so existing
// importers keep their path. Walls/Void/half walls stop bullets; cubicle
// partitions are penetrable (see TILE_TRAITS in tiles.ts).
export { tileStopsBullet };

/**
 * True if there's a clear line of FIRE from (ax, ay) to (bx, by): no
 * bullet-stopping tile (wall / half wall / Void) on the interior Bresenham
 * line. Cubicle partitions are penetrable, so they don't block. Endpoints are
 * excluded — the shooter's and target's own tiles never block.
 */
export function hasLineOfFire(map: MapData, ax: number, ay: number, bx: number, by: number): boolean {
  if (ax === bx && ay === by) return true;
  const dx = Math.abs(bx - ax);
  const dy = Math.abs(by - ay);
  const sx = ax < bx ? 1 : -1;
  const sy = ay < by ? 1 : -1;
  let err = dx - dy;
  let x = ax;
  let y = ay;
  while (true) {
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
    if (x === bx && y === by) return true;
    if (!inBounds(map, x, y)) return false;
    if (tileStopsBullet(tileAt(map, x, y))) return false;
  }
}

export interface ImpactContext {
  /** 'wall' = hit geometry, 'target' = reached planned target, 'age' = timed out. */
  kind: 'wall' | 'target' | 'age';
  bullet: Bullet;
}

/**
 * Advance every live bullet by `dt` seconds. Stops bullets that enter a
 * blocking tile OR reach within 0.5 tiles of their planned target OR
 * exceed BULLET_MAX_AGE_SEC. Dead bullets get `alive=false`; the caller
 * is expected to filter them out.
 *
 * onImpact fires exactly once per bullet at end-of-life, EXCEPT for out-
 * of-bounds bullets which age out silently.
 */
export function updateBullets(
  bullets: Bullet[],
  map: MapData,
  dt: number,
  onImpact?: (ctx: ImpactContext) => void,
): void {
  for (const b of bullets) {
    if (!b.alive) continue;
    b.age += dt;
    if (b.age >= BULLET_MAX_AGE_SEC) {
      b.alive = false;
      onImpact?.({ kind: 'age', bullet: b });
      continue;
    }

    // Substep the movement so bullets can't tunnel through thin walls at
    // high speed. Cap each substep to half a tile.
    const steps = Math.max(1, Math.ceil(Math.hypot(b.vx, b.vy) * dt / 0.5));
    const subDt = dt / steps;
    let died = false;
    let prevTx = Math.floor(b.x);
    let prevTy = Math.floor(b.y);
    for (let s = 0; s < steps && !died; s++) {
      b.x += b.vx * subDt;
      b.y += b.vy * subDt;
      const tx = Math.floor(b.x);
      const ty = Math.floor(b.y);
      if (!inBounds(map, tx, ty)) {
        b.alive = false;
        died = true;
        break;
      }
      // On entering a NEW tile, chew through any destructible prop there (a
      // cubicle takes many rounds before it's shot to pieces). Damage applies
      // once per tile crossed, not per substep.
      if (tx !== prevTx || ty !== prevTy) {
        damageTile(map, tx, ty, b.plan?.damage ?? 0);
        prevTx = tx;
        prevTy = ty;
      }
      // Re-read AFTER damage (a destroyed prop becomes passable this instant).
      const t = tileAt(map, tx, ty);
      if (tileStopsBullet(t)) {
        b.alive = false;
        onImpact?.({ kind: 'wall', bullet: b });
        died = true;
        break;
      }
      // Reached target? Distance to target tile center < 0.5 tile.
      const distToTarget = Math.hypot(b.x - b.targetX, b.y - b.targetY);
      if (distToTarget < 0.5) {
        b.alive = false;
        onImpact?.({ kind: 'target', bullet: b });
        died = true;
        break;
      }
    }
  }
}
